/**
 * Validación de los datos de la declaración juramentada de residencia.
 *
 * No lee el PDF: revisa lo que el participante diligenció en línea, que la plataforma ya tiene
 * guardado. Por eso no cuesta nada y no puede equivocarse por mala calidad de imagen.
 *
 * Dos consecuencias distintas según lo que falle:
 *
 * - No cumplir el requisito de residencia es causal de RECHAZO, y su texto ya es oficial
 *   (rechazo b del catálogo): es un requisito habilitante, no un documento por subsanar.
 * - Una dirección incompleta o datos de contacto faltantes SÍ se pueden subsanar. Esos textos
 *   están PROPUESTOS y esperan aprobación del equipo de la convocatoria.
 */

/** Años mínimos de residencia que exige el lineamiento. */
export const ANIOS_MINIMOS_RESIDENCIA = 2;

/** Proporción de integrantes que deben residir en Medellín, igual que exige hoy la plataforma. */
export const PROPORCION_MINIMA_EN_MEDELLIN = 0.6;

export const OBSERVACIONES_DECLARACION: { [codigo: string]: string } = {
  // Oficial: rechazo b del catálogo.
  b_rechazo:
    'Se rechaza ya que el participante no cumple con el requisito de residencia establecido en las condiciones generales de participación. Declara residencia de {tiempo_residencia}.',
  b_rechazo_grupo:
    'Se rechaza ya que el grupo no cumple con el requisito de residencia establecido en las condiciones generales de participación. {detalle_incumplimiento}.',
  // PROPUESTOS: redacción pendiente de aprobación.
  u: 'La dirección registrada en la declaración de residencia está incompleta. Debe subsanar la declaración de residencia indicando la dirección completa, con el tipo de vía, el número y la nomenclatura (por ejemplo: Calle 50 # 40-20).',
  v: 'La declaración de residencia no tiene todos los datos de contacto. Debe subsanar la declaración de residencia con el correo electrónico y el teléfono del participante.',
};

const normalizar = (texto: any): string =>
  String(texto ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Ciudad válida para el requisito de residencia. Se compara normalizada: "Medellín" y "MEDELLIN" son la misma. */
export const esMedellin = (ciudad: any): boolean => {
  const limpia = normalizar(ciudad).replace(/\s+/g, '');
  return limpia === 'MEDELLIN';
};

/**
 * Tipos de vía que se aceptan en una dirección colombiana. Sirve para distinguir una dirección de
 * una palabra suelta: "cvalle" no es una dirección, "Calle 50 # 40-20" sí.
 */
const TIPOS_DE_VIA =
  /\b(CALLE|CL|CRA|CARRERA|KR|KRA|CR|AVENIDA|AV|DIAGONAL|DG|TRANSVERSAL|TV|TRV|CIRCULAR|CIRCUNVALAR|AUTOPISTA|VIA|MANZANA|MZ|VEREDA|KILOMETRO|KM)\b/;

/** Separadores de nomenclatura: el "#" de "Calle 50 # 40-20", o sus equivalentes. */
const SEPARADOR_NOMENCLATURA = /#|N°|NRO|NO\.|\d\s*-\s*\d/;

/**
 * Una dirección se considera completa cuando tiene tipo de vía, algún número y la nomenclatura.
 * Es deliberadamente tolerante con la forma de escribirla: lo que descarta son las direcciones de
 * una sola palabra o sin números, que es lo que llega mal diligenciado.
 */
export const direccionCompleta = (direccion: any): boolean => {
  const limpia = normalizar(direccion);
  if (limpia.length < 6) return false;

  return TIPOS_DE_VIA.test(limpia) && /\d/.test(limpia) && SEPARADOR_NOMENCLATURA.test(limpia);
};

/** Interpreta los años declarados. Devuelve null si no es un número. */
export const aniosDeclarados = (valor: any): number | null => {
  const numero = Number(String(valor ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(numero) && String(valor ?? '').trim() !== '' ? numero : null;
};

export type HallazgoDeclaracion = {
  codigo: string;
  estado: 'subsanar' | 'rechazar';
  observacion: string;
  confianza: number;
};

const participantes = (doc: any): any[] => (Array.isArray(doc?.participantes) ? doc.participantes : []);

/**
 * Revisa el requisito de residencia de un grupo: la plataforma exige que al menos el 60% de los
 * integrantes resida en Medellín.
 */
export const residenciaDelGrupo = (doc: any): { cumple: boolean; enMedellin: number; total: number } => {
  const lista = participantes(doc);
  const enMedellin = lista.filter((p: any) => esMedellin(p?.ciudad)).length;
  const minimo = Math.ceil(lista.length * PROPORCION_MINIMA_EN_MEDELLIN);

  return { cumple: lista.length > 0 && enMedellin >= minimo, enMedellin, total: lista.length };
};

/**
 * Valida los datos diligenciados de la declaración de residencia.
 *
 * Solo se pronuncia sobre lo que está diligenciado: si la declaración está vacía, de eso se encarga
 * la regla j y aquí no se agrega nada.
 */
export const verificarDeclaracionResidencia = (doc: any): HallazgoDeclaracion[] => {
  const hallazgos: HallazgoDeclaracion[] = [];
  const esDeGrupo = participantes(doc).length > 0;

  if (esDeGrupo) {
    const { cumple, enMedellin, total } = residenciaDelGrupo(doc);
    if (!cumple) {
      hallazgos.push({
        codigo: 'b_rechazo_grupo',
        estado: 'rechazar',
        observacion: OBSERVACIONES_DECLARACION.b_rechazo_grupo.replace(
          '{detalle_incumplimiento}',
          `Solo ${enMedellin} de ${total} integrantes declaran residencia en Medellín, y se exige al menos el ${Math.round(
            PROPORCION_MINIMA_EN_MEDELLIN * 100
          )}%`
        ),
        confianza: 1,
      });
    }
    return hallazgos;
  }

  // Declaración individual: solo se evalúa si el participante alcanzó a diligenciarla.
  const tieneDatos = String(doc?.ciudad ?? '').trim() !== '' || String(doc?.direccion ?? '').trim() !== '';
  if (!tieneDatos) return hallazgos;

  const anios = aniosDeclarados(doc?.anos_residencia);
  const residenciaInsuficiente = anios !== null && anios < ANIOS_MINIMOS_RESIDENCIA;

  if (!esMedellin(doc?.ciudad) || residenciaInsuficiente) {
    const declarado = anios !== null ? `${anios} año(s) en ${String(doc?.ciudad ?? '').trim()}` : String(doc?.ciudad ?? '').trim();
    hallazgos.push({
      codigo: 'b_rechazo',
      estado: 'rechazar',
      observacion: OBSERVACIONES_DECLARACION.b_rechazo.replace('{tiempo_residencia}', declarado),
      confianza: 1,
    });
  }

  if (!direccionCompleta(doc?.direccion)) {
    hallazgos.push({ codigo: 'u', estado: 'subsanar', observacion: OBSERVACIONES_DECLARACION.u, confianza: 1 });
  }

  const sinContacto =
    String(doc?.correo ?? '').trim() === '' || String(doc?.telefono ?? '').trim() === '';
  if (sinContacto) {
    hallazgos.push({ codigo: 'v', estado: 'subsanar', observacion: OBSERVACIONES_DECLARACION.v, confianza: 1 });
  }

  return hallazgos;
};
